import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Clock, Car, Bus, Camera, Calendar } from 'lucide-react';
import PhotosCarousel from './PhotosCarousel';
import MiniMap from './MiniMap';
import ParkingPhotoButton from './ParkingPhotoButton';
import GoogleMapsButton from './GoogleMapsButton';

const SpotArticle = ({ spot, isPreview = false }) => {
  if (!spot) return null;

  return (
    <article className="max-w-4xl mx-auto">
      {/* Carousel d'images principal */}
      <div className="mb-8">
        <PhotosCarousel images={spot.images} title={spot.title} />
      </div>

      {/* En-tête de l'article */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-amber-900 mb-4">
          {spot.title}
        </h1>
        <div className="flex items-center text-lg text-muted-foreground mb-4">
          <MapPin className="w-5 h-5 mr-2" />
          {spot.location}
        </div>
        
        {/* Meta informations */}
        <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            Publié le {new Date(spot.publishedAt).toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long', 
              day: 'numeric'
            })}
          </div>
          <div className="flex items-center">
            <Camera className="w-4 h-4 mr-1" />
            Par {spot.author}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-8">
          {spot.tags.map((tag, index) => (
            <Badge key={index} variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      {/* Description principale */}
      <div className="prose prose-lg max-w-none mb-8">
        <p className="text-xl text-muted-foreground leading-relaxed">
          {spot.description}
        </p>
      </div>

      {/* Informations pratiques */}
      <Card className="mb-8 bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center text-amber-800">
            <MapPin className="w-5 h-5 mr-2" />
            Informations pratiques
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-amber-600" />
              <span className="font-medium">Temps d'accès:</span>
              <span className="ml-2">{spot.accessTime}</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 mr-2 text-amber-600 flex items-center justify-center">📊</span>
              <span className="font-medium">Difficulté:</span>
              <span className="ml-2">{spot.difficulty}</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 mr-2 text-amber-600 flex items-center justify-center">🌅</span>
              <span className="font-medium">Meilleur moment:</span>
              <span className="ml-2">{spot.bestTime}</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <Car className="w-4 h-4 mr-2 text-amber-600" />
              <span className="font-medium">Parking:</span>
              <span className="ml-2">{spot.parking}</span>
            </div>
            <div className="flex items-center">
              <Bus className="w-4 h-4 mr-2 text-amber-600" />
              <span className="font-medium">Transport:</span>
              <span className="ml-2">{spot.publicTransport}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mini-carte avec parking et bouton Google Maps */}
      {spot.coordinates && spot.parking?.coordinates && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Localisation et accès</h2>
          <MiniMap spot={spot} parking={spot.parking} />
          
          {/* Boutons d'action */}
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <GoogleMapsButton 
              coordinates={spot.coordinates} 
              title={spot.title}
              className="flex-1"
            />
            
            {spot.parking?.photo && (
              <ParkingPhotoButton parking={spot.parking} />
            )}
          </div>
        </div>
      )}

      {/* Contenu principal de l'article */}
      {!isPreview && (
        <div className="prose prose-lg max-w-none mb-8">
          <div dangerouslySetInnerHTML={{ __html: spot.content }} />
        </div>
      )}

      {/* Section informative finale */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <h3 className="text-xl font-semibold text-amber-800 mb-3">
          Profitez de votre visite ! 🌅
        </h3>
        <p className="text-amber-700 mb-4">
          N'hésitez pas à partager vos plus belles photos de ce spot avec la communauté Sun-Spot.
        </p>
        <p className="text-sm text-amber-600">
          Respectons ensemble ces lieux exceptionnels pour les générations futures.
        </p>
      </div>
    </article>
  );
};

export default SpotArticle;